import { mediaUrl, parseMediaReference } from 'services/media';
import { DefaultRenderer } from "steem-content-renderer"; 
import Remarkable from 'remarkable';
import sanitize from 'sanitize-html';
const remarkable = new Remarkable(); 

/** Removes all markdown leaving just plain text */
const remarkableStripper = md => {
    md.renderer.render = (tokens, options, env) => {
        let str = '';
        for (let i = 0; i < tokens.length; i++) {
            if (tokens[i].type === 'inline') {
                str += md.renderer.render(tokens[i].children, options, env);
            } else {
                // console.log('content', tokens[i])
                const content = tokens[i].content;
                str += (content || '') + ' ';
            }
        }
        return str;
    };
};
export const htmlDecode = txt =>
    txt.replace(/&[a-z]+;/g, ch => {
        const char = htmlCharMap[ch.substring(1, ch.length - 1)];
        return char ? char : ch;
    });

const htmlCharMap = {
    amp: '&',
    quot: '"',
    lsquo: '‘',
    rsquo: '’',
    sbquo: '‚',
    ldquo: '“',
    rdquo: '”',
    bdquo: '„',
    hearts: '♥',
    trade: '™',
    hellip: '…',
    pound: '£',
    copy: '',
};

remarkable.use(remarkableStripper); // removes all markdown
const escapeAttribute = (value: string) => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;');

export function replaceIPFSVideoLinks(html: string): string {
  // Normalize automatic loads as well as links; the renderer may turn a bare CID URL into an image.
  let normalized = html.replace(/<(?:a|img|video|source|iframe)\b[^>]*>/gi, tag => tag.replace(
    /\b(href|src|poster)\s*=\s*(["'])(.*?)\2/gi,
    (attribute, name, _quote, value) => {
      const reference = value.replace(/&amp;/g, '&');
      if (!parseMediaReference(reference)) return attribute;
      return `${name}="${escapeAttribute(mediaUrl(reference, undefined, false) || '')}"`;
    }
  ));
  normalized = normalized.replace(/\bsrcset\s*=\s*(["'])(.*?)\1/gi, (_attribute, _quote, value) => {
    const candidates = value.split(',').map((candidate: string) => {
      const parts = candidate.trim().split(/\s+/);
      if (parseMediaReference(parts[0])) parts[0] = mediaUrl(parts[0].replace(/&amp;/g, '&'), undefined, false) || '';
      return parts.join(' ');
    });
    return 'srcset="' + escapeAttribute(candidates.join(', ')) + '"';
  });
  normalized = normalized.replace(/<a[^>]*href=["']([^"']+)["'][^>]*>\s*(?:IPFS Video|Watch(?: Video)?)\s*<\/a>/gi,
    (anchor, reference) => {
      if (!parseMediaReference(reference)) return anchor;
      const url = mediaUrl(reference.replace(/&amp;/g, '&'), undefined, false);
      if (!url) return '<span>Video unavailable</span>';
      return '<video controls playsinline preload="metadata" crossorigin="anonymous" style="max-width:100%;margin:1em 0"><source src="'
        + escapeAttribute(url) + '" type="video/mp4"></video>';
    });
  return normalized;
}

export function removePrimaryVideo(body: string, reference: string): string {
  const selected = parseMediaReference(reference);
  if (!selected || typeof body !== 'string') return body || '';
  const same = (value: string) => {
    const found = parseMediaReference(value.replace(/&amp;/g, '&').replace(/[)\]}>.,;!"']+$/, ''));
    return found?.cid === selected.cid && found?.path === selected.path;
  };
  return body
    .replace(/\[[^\]]*\]\(([^)]+)\)/g, (link, value) => same(value) ? '' : link)
    .replace(/<(a|iframe|video)\b[^>]*(?:href|src)=["']([^"']+)["'][^>]*>[\s\S]*?<\/\1>/gi,
      (tag, _name, value) => same(value) ? '' : tag)
    .replace(/<img\b[^>]*src=["']([^"']+)["'][^>]*>/gi, (tag, value) => same(value) ? '' : tag)
    .replace(/(?:https?:\/\/[^\s<>"']+\/ipfs\/|ipfs:\/\/)[^\s<>"']+/gi, value => same(value) ? '' : value);
}


/**
 * Short description - remove bold and header, links with titles.
 *
 * if `strip_quotes`, try to remove any block quotes at beginning of body.
 */
export function extractBodySummary(body, strip_quotes = false) {
    let desc = body;
    if (strip_quotes)
        desc = desc.replace(/(^(\n|\r|\s)*)>([\s\S]*?).*\s*/g, '');

    desc = remarkable.render(desc); // render markdown to html
    desc = sanitize(desc, { allowedTags: [] }); // remove all html, leaving text
    desc = htmlDecode(desc);

    // Strip any raw URLs from preview text
    desc = desc.replace(/https?:\/\/[^\s]+/g, '');

    // Grab only the first line (not working as expected. does rendering/sanitizing strip newlines?)
    desc = desc.trim().split('\n')[0];

    if (desc.length > 140) {
        desc = desc.substring(0, 140).trim();

        // Truncate, remove the last (likely partial) word (along with random punctuation), and add ellipses
        desc = desc
            .substring(0, 120)
            .trim()
            .replace(/[,!\?]?\s+[^\s]+$/, '…');
    }

    return desc;
}


const renderer = new DefaultRenderer({
  baseUrl: "https://speem.watch/",
  breaks: true,
  skipSanitization: false,
  allowInsecureScriptTags: false,
  addNofollowToLinks: true,
  doNotShowImages: false,
  ipfsPrefix: "",
  assetsWidth: 640,
  assetsHeight: 480,
  imageProxyFn: (url: string) => url,
  usertagUrlFn: (account: string) => "/u/" + account,
  hashtagUrlFn: (hashtag: string) => "/new/" + hashtag,
  isLinkSafeFn: () => true, 
});

const originalRender = renderer.render.bind(renderer);
renderer.render = (body: string) => replaceIPFSVideoLinks(originalRender(body));
export default renderer;